import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import AppContainer from "../Structure/AppContainer";
import Select from "react-select";
import AuthContext from "../../Context/AuthContext";
import ajaxRequisitions from "../../util/remote/ajaxRequisitions";
import useStateCallback from "../../util/customHooks/useStateCallback";
import RequisitionDetailsModal from "./RequisitionDetailsModal";
import ajaxDepartments from "../../util/remote/ajaxDepartments";
import functions from "../../util/functions";

const ViewRequisitions = () => {
  const { userId } = useContext(AuthContext);
  const DeptID = functions.DeptGuard(); // Department guard (could be null or string)
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [meta, setMeta] = useState([]);
  const [page, setPage] = useState(1);
  const [deptID, setDeptID] = useState("");
  const [departments, setDepartments] = useState([]);

  // Date filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    const today = new Date();
    setToDate(today.toISOString().split("T")[0]); // YYYY-MM-DD
  }, []);

  // Modals
  const [view_details, setView_details] = useStateCallback(false);

  // Fetch departments
  const fetch_departmentList = async () => {
    const server_response = await ajaxDepartments.fetchDepartmentsList({});
    if (server_response.status === "OK") {
      const deptList = server_response.details.map((d) => ({
        value: d.id,
        label: d.department,
      }));
      setDepartments(deptList);

      // ✅ If DeptID exists, prefill the department and lock it
      if (DeptID) {
        const selected = deptList.find((d) => d.value === DeptID);
        if (selected) setDeptID(selected);
      }
    } else {
      toast.error(server_response.message);
    }
  };

  useEffect(() => {
    fetch_departmentList();
  }, []);

  // Fetch requisitions
  const fetchRequisitionsList = async () => {
    setLoading(true);
    const data = {
      page,
      from_date: fromDate,
      to_date: toDate,
      deptID: DeptID || deptID?.value || "",
    };
    setRequisitions([]);
    setMeta([]);
    const server_response = await ajaxRequisitions.fetchRequisitionsList(data);
    setLoading(false);
    if (server_response.status === "OK") {
      setRequisitions(server_response.details.list);
      setMeta(server_response.details.meta.list_of_pages);
    } else {
      toast.error(server_response.message);
    }
  };

  useEffect(() => {
    fetchRequisitionsList();
  }, [page]);

  // Pagination
  const setNextPageNumber = () => {
    if (page < meta.length) setPage(page + 1);
  };
  const setPreviousPageNumber = () => {
    if (page > 1) setPage(page - 1);
  };
  const setPageNumber = (e, item) => {
    e.preventDefault();
    setPage(item);
  };

  // Modal handlers
  const handleView_details = (data) => {
    setView_details(false, () =>
      setView_details(<RequisitionDetailsModal data={data} />)
    );
  };

  // Filters
  const handleFilter = () => {
    setPage(1);
    fetchRequisitionsList();
  };

  const handleResetFilters = () => {
    setFromDate("");
    const today = new Date().toISOString().split("T")[0];
    setToDate(today);
    if (!DeptID) setDeptID("");
    setPage(1);
    fetchRequisitionsList();
  };

  return (
    <AppContainer title="Requisitions View">
      <div className="container-fluid">
        <Toaster />
        {/* Modals */}
        {view_details}

        {/* Create button */}
        <div className="row mb-3">
          <div className="col-lg-3">
            <button
              className="btn btn-primary shadow-sm"
              onClick={() => navigate("/requisition/create")}
            >
              <i className="fa fa-plus me-2"></i> Create Requisition
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-header bg-light fw-bold">
            <i className="fa fa-filter me-2"></i> Filter Requisitions
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-lg-3">
                <label className="form-label">From Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="col-lg-3">
                <label className="form-label">To Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Department</label>
                <Select
                  options={departments}
                  value={
                    DeptID
                      ? departments.find((d) => d.value === DeptID) || null
                      : deptID
                  }
                  onChange={setDeptID}
                  placeholder="Select Department"
                  isDisabled={!!DeptID} // ✅ disable if department is fixed
                />
              </div>
              <div className="col-lg-2 d-flex align-items-end">
                <button className="btn btn-success me-2" onClick={handleFilter}>
                  <i className="fa fa-search me-1"></i> Filter
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleResetFilters}
                >
                  <i className="fa fa-undo me-1"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card shadow-sm border-0">
          <div className="card-header bg-light fw-bold">
            <i className="fa fa-list me-2"></i> Organisation Requisitions
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Narration</th>
                    <th>Department</th>
                    <th>Est. Total</th>
                    <th>Approval Level</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(requisitions) && requisitions.length > 0 ? (
                    requisitions.map((req, key) => (
                      <tr key={key}>
                        <td>{key + 1}</td>
                        <td>{req.naration}</td>
                        <td>{req.department}</td>
                        <td>
                          <span className="badge bg-info">{req.est_total}</span>
                        </td>
                        <td>{req.approval_level}</td>
                        <td>
                          {req.status === "10" ? (
                            <span className="badge bg-success">
                              <i className="fa fa-check-circle me-1"></i>{" "}
                              Approved
                            </span>
                          ) : (
                            <span className="badge bg-danger">
                              {req.status} / {req.approval_level} Approved
                            </span>
                          )}
                        </td>
                        <td>{req.date_created}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-info me-2"
                            onClick={() => handleView_details(req)}
                          >
                            <i className="fa fa-eye"></i> Details
                          </button>
                          {/* Update button */}
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              navigate("/requisition/update", { state: req })
                            }
                          >
                            <i className="fa fa-edit"></i> Update
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-3">
                        {loading
                          ? "Loading requisitions..."
                          : "No records found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="card-footer bg-light">
            <nav className="d-flex justify-content-center">
              <ul className="pagination mb-0">
                <li className="page-item">
                  <button className="page-link" onClick={setPreviousPageNumber}>
                    Prev
                  </button>
                </li>
                {Array.isArray(meta) &&
                  meta.map((item) =>
                    page === item ? (
                      <li className="page-item active" key={item}>
                        <span className="page-link">{item}</span>
                      </li>
                    ) : (
                      <li className="page-item" key={item}>
                        <a
                          className="page-link"
                          href="#"
                          onClick={(e) => setPageNumber(e, item)}
                        >
                          {item}
                        </a>
                      </li>
                    )
                  )}
                <li className="page-item">
                  <button className="page-link" onClick={setNextPageNumber}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </AppContainer>
  );
};

export default ViewRequisitions;
